// Parsers for the four my.Scouting / Scoutbook CSV exports that feed Unit
// Health. Each returns rows already resolved to a Compass districtId, plus
// report-level metadata. Rows whose district can't be matched are dropped
// into `unmatched` for the import preview.

import { parseCsvRecords, findPreambleValue } from './csv';
import {
  matchDistrictId,
  normalizeUnitName,
  unitTypeFromName,
  usDateToIso,
  yptStatusFromText,
  type ISODateString,
  type LeaderScope,
  type NamedRecord,
  type UnitKey3Person,
  type UnitReportType,
  type UnitType,
  type YptStatus,
} from '../types';

export interface ParsedRow {
  districtId: string;
  scoutbookUnitId: string | null;
  name: string;
  normalizedName: string;
  type: UnitType;
  charterOrg: string | null;
}

export interface RechartRow extends ParsedRow {
  charterExpires: ISODateString;
  paidYouth: number;
  paidAdults: number;
  scoutLifeYouth: number;
  scoutLifeAdults: number;
}

export interface CharterOrgRow extends ParsedRow {
  orgTypeCode: string | null;
  orgType: string | null;
  orgTypeShort: string | null;
  address: string | null;
  cityState: string | null;
  zip: string | null;
  phone: string | null;
  tenureMonths: number | null;
  genderAccepted: string | null;
  expiryDate: ISODateString;
  paidYouth: number;
  paidAdults: number;
  specialInterestCode: string | null;
  specialInterestType: string | null;
  cisCouncilPaid: boolean;
}

export interface Key3Row extends ParsedRow {
  hasCommitteeChair: boolean;
  hasUnitLeader: boolean;
  hasCharterOrgRep: boolean;
  worstYpt: YptStatus;
  people: UnitKey3Person[];
}

export interface AdvancementRow extends ParsedRow {
  youth: number;
  ranksYtd: number;
  eaglesYtd: number;
  meritBadgesYtd: number;
  byRank: Record<string, number>;
}

export interface ParsedReport<T extends ParsedRow> {
  reportType: Exclude<UnitReportType, 'trainedLeaders' | 'syt'>;
  reportDate: ISODateString;
  programYear: string | null; // set for advancement reports
  rows: T[];
  unmatched: string[]; // human-readable, for the preview
}

function reportDate(text: string): ISODateString {
  const raw = findPreambleValue(text, 'Date Report Generated');
  return usDateToIso(raw ?? '') ?? new Date().toISOString().slice(0, 10);
}

function programYearFromWindow(text: string): string | null {
  // "Year-to-Date (YTD):: From 01/01/2026 to 12/31/2026"
  const raw = findPreambleValue(text, 'Year-to-Date') ?? findPreambleValue(text, 'Reporting Period');
  const m = (raw ?? '').match(/(\d{4})/);
  return m ? m[1] : null;
}

const n = (s: string | undefined) => {
  const v = Number(String(s ?? '').replace(/[^\d.-]/g, ''));
  return Number.isFinite(v) ? v : 0;
};

function baseRow(
  districtName: string,
  unitName: string,
  districts: NamedRecord[],
  scoutbookUnitId: string | null,
  charterOrg: string | null,
): ParsedRow | null {
  const districtId = matchDistrictId(districtName, districts);
  const name = unitName.trim();
  if (!districtId || !name) return null;
  return {
    districtId,
    scoutbookUnitId: scoutbookUnitId || null,
    name,
    normalizedName: normalizeUnitName(name),
    type: unitTypeFromName(name),
    charterOrg: charterOrg?.trim() || null,
  };
}

// A "unit designation" like "Pack 0011 (F)" — the join key. A council- or
// district-total row (empty unit id, name equal to the district or council)
// is not a real unit.
function isRealUnit(name: string, districtName: string): boolean {
  const n2 = name.trim().toLowerCase();
  return (
    !!n2 &&
    n2 !== districtName.trim().toLowerCase() &&
    /^(pack|troop|crew|ship|post|club)\s/.test(n2)
  );
}

// ---- recharter --------------------------------------------------------

export function parseRecharterReport(
  text: string,
  districts: NamedRecord[],
): ParsedReport<RechartRow> {
  const recs = parseCsvRecords(text, ['districtname', 'unitname', 'strexpiredunitdate']);
  const rows: RechartRow[] = [];
  const unmatched: string[] = [];
  for (const r of recs) {
    if (!isRealUnit(r.unitname, r.districtname)) continue;
    const base = baseRow(r.districtname, r.unitname, districts, null, r.communityorganization);
    const expires = usDateToIso(r.strexpiredunitdate);
    if (!base || !expires) {
      unmatched.push(`${r.districtname} · ${r.unitname}`);
      continue;
    }
    rows.push({
      ...base,
      charterExpires: expires,
      paidYouth: n(r.paidmembers),
      paidAdults: n(r.paidadults),
      scoutLifeYouth: n(r.scoutlifesubscriptions),
      scoutLifeAdults: n(r.adultsscoutlifesubscriptions),
    });
  }
  return { reportType: 'recharter', reportDate: reportDate(text), programYear: null, rows, unmatched };
}

// ---- chartered organizations -------------------------------------------

export function parseCharterOrgReport(
  text: string,
  districts: NamedRecord[],
): ParsedReport<CharterOrgRow> {
  const recs = parseCsvRecords(text, ['districtname', 'unitname', 'communityorganizationtypecode', 'tenure']);
  const rows: CharterOrgRow[] = [];
  const unmatched: string[] = [];
  for (const r of recs) {
    if (!isRealUnit(r.unitname, r.districtname)) continue;
    // "Troop 0014" is already a complete, pre-formatted unit designation —
    // append the gender suffix Compass's own Unit.name convention expects
    // ("Troop 0014 (B)") so a linked Boy/Girl pair resolves to its correct,
    // distinct unit even on a first-ever match (scoutbookUnitId below is
    // the reliable path once a unit has been seen before — this is only
    // the fallback for a genuinely new one). Same technique
    // parseCommissionerAssignmentReport already uses for the same reason.
    const gender = (r.genderaccepted ?? '').trim().toUpperCase();
    const unitName = ['F', 'B', 'G'].includes(gender) ? `${r.unitname.trim()} (${gender})` : r.unitname.trim();
    const base = baseRow(r.districtname, unitName, districts, r.unitid || null, r.communityorganization);
    const expires = usDateToIso(r.expirydtstr);
    if (!base || !expires) {
      unmatched.push(`${r.districtname} · ${r.unitname}`);
      continue;
    }
    rows.push({
      ...base,
      orgTypeCode: r.communityorganizationtypecode?.trim() || null,
      orgType: r.communityorganizationtype?.trim() || null,
      orgTypeShort: r.communityorganizationtypeshort?.trim() || null,
      address: r.address?.trim() || null,
      cityState: r.citystate?.trim() || null,
      zip: r.zip?.trim() || null,
      phone: r.phone?.trim() || null,
      tenureMonths: r.tenure ? n(r.tenure) : null,
      genderAccepted: gender || null,
      expiryDate: expires,
      paidYouth: n(r.qttyouth),
      paidAdults: n(r.qttadultvolunteers),
      specialInterestCode: r.specialinteresttypecode?.trim() || null,
      specialInterestType: r.specialinteresttype?.trim() || null,
      cisCouncilPaid: (r.ciscouncilpaid ?? '').trim().toLowerCase() === 'yes',
    });
  }
  return { reportType: 'charterOrg', reportDate: reportDate(text), programYear: null, rows, unmatched };
}

// ---- Key 3 / YPT -----------------------------------------------------

const LEADER_POSITIONS = /cubmaster|scoutmaster|advisor|skipper/i;

export function parseKey3Report(text: string, districts: NamedRecord[]): ParsedReport<Key3Row> {
  const recs = parseCsvRecords(text, ['districtname', 'fullname', 'position', 'yptstatus']);
  // group people by unit designation parsed out of "unitcommorgname"
  const byUnit = new Map<
    string,
    { districtName: string; unitName: string; charterOrg: string | null; people: UnitKey3Person[] }
  >();
  for (const r of recs) {
    const combo = r.unitcommorgname ?? '';
    const dash = combo.indexOf(' - ');
    const unitName = (dash >= 0 ? combo.slice(0, dash) : combo).trim();
    const charterOrg = dash >= 0 ? combo.slice(dash + 3).trim() : null;
    if (!isRealUnit(unitName, r.districtname)) continue;
    const key = `${r.districtname}|||${normalizeUnitName(unitName)}`;
    const entry =
      byUnit.get(key) ??
      { districtName: r.districtname, unitName, charterOrg, people: [] as UnitKey3Person[] };
    entry.people.push({
      name: r.fullname.replace(/\s+/g, ' ').trim(),
      position: r.position.trim(),
      email: r.email.trim(),
      phone: r.phone.trim(),
      ypt: yptStatusFromText(r.yptstatus),
      yptRaw: r.yptstatus.trim(),
    });
    byUnit.set(key, entry);
  }

  const rows: Key3Row[] = [];
  const unmatched: string[] = [];
  const rank: Record<YptStatus, number> = { active: 0, unknown: 1, expiring: 2, expired: 3 };
  for (const entry of byUnit.values()) {
    const base = baseRow(entry.districtName, entry.unitName, districts, null, entry.charterOrg);
    if (!base) {
      unmatched.push(`${entry.districtName} · ${entry.unitName}`);
      continue;
    }
    const worstYpt = entry.people.reduce<YptStatus>(
      (worst, p) => (rank[p.ypt] > rank[worst] ? p.ypt : worst),
      'active',
    );
    rows.push({
      ...base,
      hasCommitteeChair: entry.people.some((p) => /committee chair/i.test(p.position)),
      hasUnitLeader: entry.people.some((p) => LEADER_POSITIONS.test(p.position)),
      hasCharterOrgRep: entry.people.some((p) => /chartered organization rep/i.test(p.position)),
      worstYpt,
      people: entry.people,
    });
  }
  return { reportType: 'key3', reportDate: reportDate(text), programYear: null, rows, unmatched };
}

// ---- advancement -----------------------------------------------------

const CUB_RANKS = ['lion', 'tiger', 'bobcat', 'wolf', 'bear', 'webelos', 'light'] as const;
const BSA_RANKS = [
  'scout',
  'tenderfoot',
  'secondclass',
  'firstclass',
  'star',
  'life',
  'eagle',
] as const;

function parseAdvancement(
  text: string,
  districts: NamedRecord[],
  program: 'cub' | 'scoutsBSA',
): ParsedReport<AdvancementRow> {
  const ranks = program === 'cub' ? CUB_RANKS : BSA_RANKS;
  const sig =
    program === 'cub'
      ? ['distorgname', 'organizationname', 'unitid', 'ranksytd', 'youthqtt']
      : ['distorgname', 'organizationname', 'unitid', 'totalytd', 'youthqtt'];
  const recs = parseCsvRecords(text, sig);
  const rows: AdvancementRow[] = [];
  const unmatched: string[] = [];
  for (const r of recs) {
    const unitName = r.organizationname ?? '';
    if (!isRealUnit(unitName, r.distorgname) || !r.unitid) continue;
    const base = baseRow(r.distorgname, unitName, districts, r.unitid, null);
    if (!base) {
      unmatched.push(`${r.distorgname} · ${unitName}`);
      continue;
    }
    const byRank: Record<string, number> = {};
    for (const rk of ranks) byRank[rk] = n(r[`${rk}ytd`]);
    rows.push({
      ...base,
      youth: n(r.youthqtt),
      ranksYtd: program === 'cub' ? n(r.ranksytd) : n(r.totalytd),
      eaglesYtd: program === 'cub' ? 0 : n(r.eagleytd),
      meritBadgesYtd: program === 'cub' ? 0 : n(r.meritbadgesytd),
      byRank,
    });
  }
  return {
    reportType: program === 'cub' ? 'advancementCub' : 'advancementScoutsBSA',
    reportDate: reportDate(text),
    programYear: programYearFromWindow(text) ?? String(new Date().getFullYear()),
    rows,
    unmatched,
  };
}

export const parseCubAdvancementReport = (t: string, d: NamedRecord[]) =>
  parseAdvancement(t, d, 'cub');
export const parseScoutsBsaAdvancementReport = (t: string, d: NamedRecord[]) =>
  parseAdvancement(t, d, 'scoutsBSA');

// ---- Trained Leaders Status ----------------------------------------

export interface TrainedLeaderRow {
  memberId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  zip: string;
  program: string | null;
  scope: LeaderScope;
  districtId: string | null;
  unitDesignation: string | null; // "Pack 0011 (F)" reconstructed
  normalizedUnitName: string | null;
  charterOrg: string | null;
  position: string;
  trained: boolean;
  directContact: boolean;
  incompleteCourses: string[];
  registrationExpires: ISODateString | null;
}

export interface ParsedLeaderReport {
  reportType: 'trainedLeaders';
  reportDate: ISODateString;
  rows: TrainedLeaderRow[];
  unmatched: string[];
}

const splitCourses = (s: string) =>
  s
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean);

export function parseTrainedLeadersReport(
  text: string,
  districts: NamedRecord[],
): ParsedLeaderReport {
  const recs = parseCsvRecords(text, [
    'memberid',
    'position',
    'trained',
    'registration_expiration_date',
  ]);
  const rows: TrainedLeaderRow[] = [];
  const unmatched: string[] = [];
  for (const r of recs) {
    const memberId = r.memberid?.trim();
    const position = r.position?.trim();
    if (!memberId || !position) continue;

    const districtRaw = r.district?.trim() ?? '';
    const unitRaw = r.unit?.trim() ?? '';
    const districtId = districtRaw ? matchDistrictId(districtRaw, districts) : null;

    let scope: LeaderScope;
    if (!districtRaw && !unitRaw) scope = 'council';
    else if (districtRaw && !unitRaw) scope = 'district';
    else scope = 'unit';

    if (scope !== 'council' && !districtId) {
      unmatched.push(`${districtRaw || '?'} · ${unitRaw || position} · ${r.first_name} ${r.last_name}`);
      continue;
    }

    let unitDesignation: string | null = null;
    if (scope === 'unit') {
      const gender = r.gender_accepted?.trim().toUpperCase();
      unitDesignation = ['F', 'B', 'G'].includes(gender ?? '') ? `${unitRaw} (${gender})` : unitRaw;
    }

    const first = (r.first_name ?? '').trim();
    const last = (r.last_name ?? '').trim();
    rows.push({
      memberId,
      firstName: first,
      lastName: last,
      fullName: [first, (r.middle_name ?? '').trim(), last].filter(Boolean).join(' '),
      email: (r.email ?? '').trim(),
      zip: (r.zip_code ?? '').trim(),
      program: (r.program ?? '').trim() || null,
      scope,
      districtId,
      unitDesignation,
      normalizedUnitName: unitDesignation ? normalizeUnitName(unitDesignation) : null,
      charterOrg: (r.chartered_org_name ?? '').trim() || null,
      position,
      trained: (r.trained ?? '').trim().toUpperCase() === 'YES',
      directContact: (r.direct_contact_leader ?? '').trim().toUpperCase() === 'YES',
      incompleteCourses: [
        ...splitCourses(r.incomplete_mandatory ?? ''),
        ...splitCourses(r.incomplete_classroom ?? ''),
        ...splitCourses(r.incomplete_online ?? ''),
      ],
      registrationExpires: usDateToIso(r.registration_expiration_date ?? ''),
    });
  }
  return { reportType: 'trainedLeaders', reportDate: reportDate(text), rows, unmatched };
}

// ---- Safeguarding Youth Training Aging Report ----------------------

export interface SytPersonRow {
  memberId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string | null;
  isCurrent: boolean;
  expiresOn: ISODateString | null;
  completedOn: ISODateString | null;
  modulesToDo: string[];
  districtIds: string[];
}

export interface ParsedSytReport {
  reportType: 'syt';
  reportDate: ISODateString;
  rows: SytPersonRow[];
  unmatched: string[];
}

/** "To Do: SCO_3010 Bullying, SCO_3008 Overview and Policies, …" ->
 *  ["Bullying", "Overview and Policies", …]. "Completed: …" and "" -> []. */
function parseSytModules(raw: string): string[] {
  const t = raw.trim();
  if (!/^to do:/i.test(t)) return [];
  return t
    .replace(/^to do:\s*/i, '')
    .split(',')
    .map((m) => m.trim().replace(/^SCO_\d+\s+/i, '').trim())
    .filter(Boolean);
}

export function parseSytReport(text: string, districts: NamedRecord[]): ParsedSytReport {
  const recs = parseCsvRecords(text, [
    'memberid',
    'isyptcurrent2',
    'yptexpirationdatec',
    'positionname',
  ]);
  // SYT is person-level; a person has one row per registered position. Merge
  // them, unioning the districts they appear under.
  const byMember = new Map<string, SytPersonRow>();
  const unmatched: string[] = [];
  for (const r of recs) {
    const memberId = r.memberid?.trim();
    if (!memberId) continue;

    const districtRaw = r.district?.trim() ?? '';
    const districtId = districtRaw ? matchDistrictId(districtRaw, districts) : null;
    if (districtRaw && !districtId) {
      unmatched.push(`${districtRaw} · ${r.firstname} ${r.lastname}`);
    }

    const existing = byMember.get(memberId);
    if (existing) {
      if (districtId && !existing.districtIds.includes(districtId)) {
        existing.districtIds.push(districtId);
      }
      continue;
    }

    const first = (r.firstname ?? '').trim();
    const last = (r.lastname ?? '').trim();
    byMember.set(memberId, {
      memberId,
      firstName: first,
      lastName: last,
      fullName: [first, (r.middlename ?? '').trim(), last].filter(Boolean).join(' '),
      email: (r.emailaddress ?? '').trim(),
      phone: (r.phonenumber ?? '').trim() || null,
      isCurrent: (r.isyptcurrent2 ?? '').trim().toUpperCase() === 'YES',
      expiresOn: usDateToIso(r.yptexpirationdatec ?? ''),
      completedOn: usDateToIso(r.y01completiondatec ?? ''),
      modulesToDo: parseSytModules(r.stronlinecourses ?? ''),
      districtIds: districtId ? [districtId] : [],
    });
  }
  return {
    reportType: 'syt',
    reportDate: reportDate(text),
    rows: [...byMember.values()],
    unmatched,
  };
}

// ---- BSA Connections: Assigned / Unassigned Report --------------------
// Current-state per unit — overwritten each import, like recharter/Key 3.
// Unlike those, it never touches the manual `commissionerContactId`
// assignment made on the Commissioners page; it only writes a separate
// `commissionerImportSuggestion` the page can compare against by name.

export interface CommissionerAssignmentRow extends ParsedRow {
  assignedName: string | null; // null when the report itself shows unassigned
  assignedMemberId: string | null;
  registrationExpired: boolean; // any of the person's Expired_Position flags == Y
  unitExpired: boolean;
  unitExpiredDate: ISODateString | null;
  lastConnectionDate: ISODateString | null;
  metricsMet: number;
  lastAssessmentDate: ISODateString | null; // "Last_Contact" — a distinct, rarer field from lastConnectionDate
  lastAssessmentScore: number | null;
}

export function parseCommissionerAssignmentReport(
  text: string,
  districts: NamedRecord[],
): ParsedReport<CommissionerAssignmentRow> {
  const recs = parseCsvRecords(text, [
    'unit_id',
    'assigned_commissioner',
    'metric_summary',
    'expired_unit',
  ]);
  const rows: CommissionerAssignmentRow[] = [];
  const unmatched: string[] = [];
  for (const r of recs) {
    const districtName = r.district_name ?? '';
    const type = (r.unit_type ?? '').trim();
    const number = (r.unit_number ?? '').trim();
    if (!type || !number) continue;
    const gender = (r.gender_accepted ?? '').trim().toUpperCase();
    const unitName = `${type} ${number.padStart(4, '0')}${
      ['F', 'B', 'G'].includes(gender) ? ` (${gender})` : ''
    }`;
    const base = baseRow(districtName, unitName, districts, r.unit_id || null, r.chartered_organization);
    if (!base) {
      unmatched.push(`${districtName} · ${unitName}`);
      continue;
    }
    const registrationExpired = (r.expired_position ?? '')
      .split(',')
      .some((s) => s.trim().toUpperCase() === 'Y');
    const score = Number(r.last_assessment_score);
    rows.push({
      ...base,
      assignedName: (r.assigned_commissioner ?? '').trim() || null,
      assignedMemberId: (r.ass__comm__member_id ?? '').trim() || null,
      registrationExpired,
      unitExpired: (r.expired_unit ?? '').trim().toUpperCase() === 'YES',
      unitExpiredDate: usDateToIso(r.expired_unit_date ?? ''),
      lastConnectionDate: usDateToIso(r.last_connection_date ?? ''),
      metricsMet: n(r.metric_summary),
      lastAssessmentDate: usDateToIso(r.last_contact ?? ''),
      lastAssessmentScore: Number.isFinite(score) && score > 0 ? score : null,
    });
  }
  return {
    reportType: 'commissionerAssignment',
    reportDate: reportDate(text),
    programYear: null,
    rows,
    unmatched,
  };
}

// ---- BSA Connections: Connections History Report -----------------------
// A growing visit log, not a snapshot — see importConnectionsHistory() in
// lib/units.ts for how re-imports add rather than replace. Rows with no
// Connection_ID are units that have never had a connection logged; they
// still carry the unit's current Metric_Summary (identical to the
// Assigned/Unassigned report's figure) but produce no connection record.

export interface ConnectionsHistoryRow {
  districtId: string;
  unitDesignation: string; // this report already gives the built name, e.g. "Troop 0007 (B)"
  normalizedUnitName: string;
  scoutbookUnitId: string | null;
  metricsMet: number;
  connectionId: string | null;
  connectionDate: ISODateString | null;
  completeDate: ISODateString | null;
  status: string;
  loggedByName: string;
  loggedByMemberId: string;
  comments: { category: string; text: string }[];
}

export interface ParsedConnectionsReport {
  reportType: 'connectionsHistory';
  reportDate: ISODateString;
  rows: ConnectionsHistoryRow[];
  unmatched: string[];
}

// Column key -> display category. Names are the report's own section
// labels, cleaned up for display — not Jason's specific 5-category list,
// since that list doesn't map 1:1 onto the actual columns (e.g. there's no
// literal "unit leader needs" column; the closest is "Other" with a
// free-typed sub-category, which the sample data shows used for exactly
// that). Keeping every real category avoids guessing at a lossy mapping.
const CONNECTION_COMMENT_COLUMNS: { key: string; category: string }[] = [
  { key: 'adult_leader_training___comment', category: 'Adult Leader Training' },
  { key: 'youth_membership__growth____comment', category: 'Youth Membership Growth' },
  { key: 'unit_size__youth____comment', category: 'Unit Size (Youth)' },
  { key: 'advancement___comment', category: 'Advancement' },
  { key: 'outdoor_activities___comment', category: 'Outdoor Activities' },
  { key: 'retention___comment', category: 'Retention' },
];

export function parseConnectionsHistoryReport(
  text: string,
  districts: NamedRecord[],
): ParsedConnectionsReport {
  const recs = parseCsvRecords(text, [
    'unit_id',
    'connection_id',
    'metric_summary__the_number_of_metrics_met_',
    'status',
  ]);
  const rows: ConnectionsHistoryRow[] = [];
  const unmatched: string[] = [];
  for (const r of recs) {
    const districtName = r.district ?? '';
    const unitName = (r.unit ?? '').trim();
    if (!unitName) continue;
    const districtId = matchDistrictId(districtName, districts);
    if (!districtId) {
      unmatched.push(`${districtName} · ${unitName}`);
      continue;
    }

    const comments: { category: string; text: string }[] = [];
    for (const { key, category } of CONNECTION_COMMENT_COLUMNS) {
      const c = (r[key] ?? '').trim();
      if (c) comments.push({ category, text: c });
    }
    const otherComment = (r.other_comment ?? '').trim();
    if (otherComment) {
      comments.push({ category: (r.other_subcategories ?? '').trim() || 'Other', text: otherComment });
    }

    rows.push({
      districtId,
      unitDesignation: unitName,
      normalizedUnitName: normalizeUnitName(unitName),
      scoutbookUnitId: r.unit_id?.trim() || null,
      metricsMet: n(r.metric_summary__the_number_of_metrics_met_),
      connectionId: r.connection_id?.trim() || null,
      connectionDate: usDateToIso(r.connection_date ?? ''),
      completeDate: usDateToIso(r.complete_date ?? ''),
      status: (r.status ?? '').trim(),
      loggedByName: [r.first_name, r.last_name].filter(Boolean).join(' ').trim(),
      loggedByMemberId: (r.member_id ?? '').trim(),
      comments,
    });
  }
  return { reportType: 'connectionsHistory', reportDate: reportDate(text), rows, unmatched };
}
